import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import TitleBlock from "@/components/TitleBlock";
import Callout from "@/components/Callout";
import { fmtNumber } from "@/lib/format";
import { RefreshCw, ExternalLink, AlertTriangle, AlertCircle, Info, Filter } from "lucide-react";

const SEV_META = {
  high:   { color: "#b91c1c", bg: "#FBE8E8", label: "High",   icon: AlertCircle },
  medium: { color: "#B47700", bg: "#FFF6DE", label: "Medium", icon: AlertTriangle },
  low:    { color: "#1D57A5", bg: "#EAF1FB", label: "Low",    icon: Info },
  info:   { color: "#5b6776", bg: "#EEEDEA", label: "Info",   icon: Info },
};
const CODE_LABEL = {
  noindex_should_index: "No-indexed but should be indexed",
  noindex_ok: "No-index (intentional)",
  canonical_points_elsewhere: "Canonical points to another URL",
  missing_meta_description: "Missing meta description",
  short_meta_description: "Short meta description",
  missing_title: "Missing <title>",
  long_title: "Title >70 chars",
  author_mismatch: "Author entity mismatch",
  author_missing: "Author meta missing",
  orphan_page: "Orphan page",
  internal_link_off_sitemap: "Link to non-sitemap URL",
  redirect: "Redirect",
  http_error: "HTTP error",
  fetch_error: "Fetch error",
};

export default function SeoPageHealthPage() {
  const [pages, setPages] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sync, setSync] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sevFilter, setSevFilter] = useState("");      // "" = all
  const [codeFilter, setCodeFilter] = useState("");
  const [hideOffSitemap, setHideOffSitemap] = useState(true);

  const load = () => api.get("/seo/page-health").then((r) => {
    setPages(r.data.pages || []);
    setSummary(r.data.summary);
    setSync(r.data.sync);
  });
  useEffect(() => { load(); }, []);

  const runCrawl = async () => {
    setBusy(true);
    try { await api.post("/seo/page-health/run"); await load(); }
    finally { setBusy(false); }
  };

  // Hide the noisiest finding by default (off-sitemap internal links).
  // These tend to be category pages / WordPress noise. Keep it filterable.
  const filteredPages = useMemo(() => {
    return pages.map((p) => ({
      ...p,
      findings: p.findings.filter((f) =>
        (!sevFilter || f.severity === sevFilter) &&
        (!codeFilter || f.code === codeFilter) &&
        (!hideOffSitemap || f.code !== "internal_link_off_sitemap")
      ),
    })).filter((p) => p.findings.length > 0);
  }, [pages, sevFilter, codeFilter, hideOffSitemap]);

  // Tile counts respect the noise filter so the tiles match what users
  // actually see when they click in. Severity-filter is NOT applied here
  // (otherwise clicking a tile would zero out every other tile's count).
  const tileCounts = useMemo(() => {
    const c = { high: 0, medium: 0, low: 0, info: 0 };
    for (const p of pages) {
      for (const f of p.findings) {
        if (hideOffSitemap && f.code === "internal_link_off_sitemap") continue;
        if (codeFilter && f.code !== codeFilter) continue;
        c[f.severity] = (c[f.severity] || 0) + 1;
      }
    }
    return c;
  }, [pages, hideOffSitemap, codeFilter]);

  const allCodes = useMemo(() => {
    const c = {};
    for (const p of pages) for (const f of p.findings) c[f.code] = (c[f.code] || 0) + 1;
    return c;
  }, [pages]);

  return (
    <div data-testid="seo-page-health-page">
      <TitleBlock
        eyebrow="SEO · weekly site audit"
        title="Page Health"
        subtitle="Every Sunday at 2 AM UTC we crawl your sitemap and run 8 health checks per page. Highest-severity findings up top. History is kept so you can verify when Railtown actually ships fixes."
        right={
          <button onClick={runCrawl} disabled={busy} data-testid="page-health-run"
                  className="btn-yellow inline-flex items-center gap-2 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
            {busy ? "Crawling…" : "Re-crawl now"}
          </button>
        }
      />
      <div className="p-6 md:p-8 space-y-6">
        <Callout title="What we check">
          <div className="text-sm text-midnight space-y-1">
            <p>We crawl your sitemap, fetch up to 60 pages, and flag:</p>
            <ul className="list-disc pl-5 text-xs opacity-90 grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <li><strong>No-index pages</strong> that should be indexed (FAQ, team, results, blog posts)</li>
              <li><strong>Canonical mismatches</strong> — pages claiming a different URL as canonical</li>
              <li><strong>Missing / short meta descriptions</strong> + missing titles</li>
              <li><strong>Author entity mismatches</strong> — blog posts authored as &quot;admin&quot; or &quot;Indie Law&quot; instead of Joey Vitale (Jason flagged this)</li>
              <li><strong>Orphan pages</strong> — in sitemap but no internal links pointing at them</li>
              <li><strong>Internal links to non-sitemap URLs</strong> — catches /home-2/-style strays</li>
            </ul>
            {sync?.last_synced && (
              <div className="text-[10px] mt-2 opacity-60">Last crawl: {new Date(sync.last_synced).toLocaleString()}</div>
            )}
          </div>
        </Callout>

        {summary && (
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-60 mb-2">Filter by severity · click a tile to filter, click again to clear</div>
            <div className="grid grid-cols-4 gap-3">
              {["high", "medium", "low", "info"].map((s) => {
                const meta = SEV_META[s];
                const Icon = meta.icon;
                const active = sevFilter === s;
                return (
                  <button key={s} onClick={() => setSevFilter(active ? "" : s)}
                          data-testid={`sev-tile-${s}`}
                          className={`p-4 border-t-4 text-left transition hover:opacity-90 ${active ? "ring-2 ring-yellow" : ""}`}
                          style={{ borderTopColor: meta.color, background: meta.bg }}>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest" style={{ color: meta.color }}>
                      <Icon className="w-3.5 h-3.5" />
                      <span style={{ fontWeight: 700 }}>{meta.label}</span>
                      {active && <span className="ml-auto bg-yellow text-midnight px-1 text-[9px]">FILTERING</span>}
                    </div>
                    <div className="text-2xl font-bold mt-1" style={{ color: meta.color }}>{tileCounts[s] || 0}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 text-xs">
          <Filter className="w-3.5 h-3.5 opacity-50" />
          <select value={codeFilter} onChange={(e) => setCodeFilter(e.target.value)}
                  data-testid="filter-code"
                  className="bg-warm border border-navy/20 px-2 py-1">
            <option value="">All finding types</option>
            {Object.entries(allCodes).sort((a, b) => b[1] - a[1]).map(([c, n]) => (
              <option key={c} value={c}>{CODE_LABEL[c] || c} ({n})</option>
            ))}
          </select>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={hideOffSitemap}
                   onChange={(e) => setHideOffSitemap(e.target.checked)}
                   data-testid="hide-off-sitemap" />
            <span className="text-midnight">Hide off-sitemap link noise</span>
          </label>
          {(sevFilter || codeFilter || !hideOffSitemap) && (
            <button onClick={() => { setSevFilter(""); setCodeFilter(""); setHideOffSitemap(true); }}
                    className="text-cobalt hover:underline">Reset filters</button>
          )}
          <span className="ml-auto opacity-60">
            {(() => {
              const findingCount = filteredPages.reduce((n, p) => n + p.findings.length, 0);
              const pageCount = filteredPages.length;
              if (findingCount === 0) return "0 findings";
              return findingCount === pageCount
                ? `${findingCount} ${findingCount === 1 ? "finding" : "findings"} on ${pageCount} ${pageCount === 1 ? "page" : "pages"}`
                : `${findingCount} findings on ${pageCount} ${pageCount === 1 ? "page" : "pages"}`;
            })()}
          </span>
        </div>

        <div className="space-y-3">
          {filteredPages.map((p) => {
            const topSev = p.findings[0]?.severity || "info";
            const meta = SEV_META[topSev];
            return (
              <div key={p.url} className="bg-warm-tint border-l-4 p-4"
                   style={{ borderLeftColor: meta.color }}
                   data-testid={`page-finding-${encodeURIComponent(p.url)}`}>
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-navy truncate">{p.title || "(no title)"}</div>
                    <a href={p.url} target="_blank" rel="noreferrer"
                       className="text-xs text-cobalt hover:underline inline-flex items-center gap-1 truncate">
                      {p.url} <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest opacity-70">
                    HTTP {p.status} · {p.findings.length} {p.findings.length === 1 ? "finding" : "findings"}
                  </span>
                </div>
                <ul className="mt-3 space-y-1">
                  {p.findings.map((f, idx) => {
                    const sm = SEV_META[f.severity];
                    const Icon = sm.icon;
                    return (
                      <li key={idx} className="flex items-start gap-2 text-xs">
                        <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: sm.color }} />
                        <div className="flex-1">
                          <strong style={{ color: sm.color }}>{CODE_LABEL[f.code] || f.code}</strong>
                          <span className="ml-2 text-midnight opacity-80">{f.message}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
          {filteredPages.length === 0 && (
            <div className="text-center text-sm opacity-60 italic py-12 border-2 border-dashed border-navy/10">
              No findings match the current filter. {fmtNumber(pages.length)} pages crawled, all clean against the selected criteria.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
